"use client"

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type PointerEvent, type ReactNode } from "react"

import { useTranslations } from "next-intl"

import { BackIcon, ChevronIcon, IconButton, Lightbox, Reel, Text } from "@/components/ui"
import { TileImage } from "@/components/ui/tile-image"
import { useReelPagination } from "@/lib/hooks/use-reel-pagination"

interface PdpGalleryViewerProps {
  images: string[]
  title: string
  index: number
  onIndexChange: (index: number) => void
  open: boolean
  onClose: () => void
}

const ZOOM = 2.2
const SWIPE_DISTANCE = 48
const DRAG_SLOP = 8

const clampPercent = (value: number) => Math.min(100, Math.max(0, value))

export const PdpGalleryViewer = ({ images, title, index, onIndexChange, open, onClose }: PdpGalleryViewerProps) => {
  const t = useTranslations("pdp")
  const [zoomed, setZoomed] = useState(false)
  const [shown, setShown] = useState(index)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setShown(index)
  }
  const stage = useRef<HTMLButtonElement>(null)
  const frame = useRef(0)
  const origin = useRef({ x: 50, y: 50 })
  const gesture = useRef<{ x: number; y: number; moved: boolean } | null>(null)
  const dragged = useRef(false)
  const paged = images.length > 1

  const show = useCallback(
    (target: number) => {
      setZoomed(false)
      setShown(target)
      onIndexChange(target)
    },
    [onIndexChange]
  )

  const step = useCallback((delta: number) => show(Math.min(images.length - 1, Math.max(0, shown + delta))), [images.length, shown, show])

  useEffect(() => {
    if (!open || !paged) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") step(-1)
      if (event.key === "ArrowRight") step(1)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, paged, step])

  useEffect(() => {
    if (!open) setZoomed(false)
    return () => cancelAnimationFrame(frame.current)
  }, [open])

  const setOrigin = (x: number, y: number) => {
    origin.current = { x: clampPercent(x), y: clampPercent(y) }
    if (stage.current) stage.current.style.transformOrigin = `${origin.current.x}% ${origin.current.y}%`
  }

  const pointOrigin = (event: MouseEvent<HTMLButtonElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    setOrigin(((event.clientX - bounds.left) / bounds.width) * 100, ((event.clientY - bounds.top) / bounds.height) * 100)
  }

  const track = (event: MouseEvent<HTMLButtonElement>) => {
    if (!zoomed) return
    cancelAnimationFrame(frame.current)
    const { currentTarget, clientX, clientY } = event
    frame.current = requestAnimationFrame(() => pointOrigin({ currentTarget, clientX, clientY } as MouseEvent<HTMLButtonElement>))
  }

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (!event.isPrimary) return
    dragged.current = false
    gesture.current = { x: event.clientX, y: event.clientY, moved: false }
    if (zoomed || event.pointerType === "mouse") event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    if (!event.isPrimary) return
    if (gesture.current && event.pointerType === "mouse" && event.buttons === 0) gesture.current = null
    const active = gesture.current
    if (!active) {
      if (event.pointerType === "mouse") track(event)
      return
    }
    const dx = event.clientX - active.x
    const dy = event.clientY - active.y
    if (!active.moved && Math.hypot(dx, dy) < DRAG_SLOP) return
    active.moved = true
    if (!zoomed) return
    const bounds = event.currentTarget.getBoundingClientRect()
    setOrigin(origin.current.x - ((dx / bounds.width) * 100) / (ZOOM - 1), origin.current.y - ((dy / bounds.height) * 100) / (ZOOM - 1))
    active.x = event.clientX
    active.y = event.clientY
  }

  const onPointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    if (!event.isPrimary) return
    const active = gesture.current
    gesture.current = null
    if (!active?.moved) return
    dragged.current = true
    if (zoomed || !paged || event.pointerType !== "mouse") return
    const dx = event.clientX - active.x
    const dy = event.clientY - active.y
    if (Math.abs(dx) >= SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1)
  }

  const onPointerCancel = () => {
    gesture.current = null
  }

  const isKeyboardActivation = (event: MouseEvent<HTMLButtonElement>) => event.detail === 0

  const insideImage = (event: MouseEvent<HTMLButtonElement>) => {
    const image = event.currentTarget.querySelector("img")
    if (!image || !image.naturalWidth || !image.naturalHeight) return true
    const box = image.getBoundingClientRect()
    const style = window.getComputedStyle(image)
    const pad = (value: string) => parseFloat(value) || 0
    const left = box.left + pad(style.paddingLeft)
    const top = box.top + pad(style.paddingTop)
    const width = box.width - pad(style.paddingLeft) - pad(style.paddingRight)
    const height = box.height - pad(style.paddingTop) - pad(style.paddingBottom)
    const fit = Math.min(width / image.naturalWidth, height / image.naturalHeight)
    const drawnWidth = image.naturalWidth * fit
    const drawnHeight = image.naturalHeight * fit
    const x = event.clientX - (left + (width - drawnWidth) / 2)
    const y = event.clientY - (top + (height - drawnHeight) / 2)
    return x >= 0 && x <= drawnWidth && y >= 0 && y <= drawnHeight
  }

  const toggleZoom = (event: MouseEvent<HTMLButtonElement>) => {
    if (dragged.current) {
      dragged.current = false
      return
    }
    if (zoomed) return setZoomed(false)
    if (!isKeyboardActivation(event) && !insideImage(event)) return onClose()
    pointOrigin(event)
    setZoomed(true)
  }

  return (
    <Lightbox
      open={open}
      onClose={onClose}
      label={t("gallery.imagesOf", { title })}
      closeLabel={t("gallery.closeViewer")}
      toolbar={
        paged ? (
          <Text as="span" variant="bodySm" family="display" tone="inherit" numeric aria-live="polite" className="text-on-scrim-muted px-200 font-semibold">
            {shown + 1}/{images.length}
          </Text>
        ) : null
      }
      footer={
        paged ? (
          <div className="mx-auto flex w-fit max-w-full gap-200 overflow-x-auto">
            {images.map((src, thumbIndex) => (
              <IconButton
                key={src}
                icon={<TileImage src={src} alt={t("gallery.viewImage", { index: thumbIndex + 1 })} fill sizes="120px" className="object-contain p-100 mix-blend-multiply" />}
                aria-label={t("gallery.viewImage", { index: thumbIndex + 1 })}
                aria-current={thumbIndex === shown}
                onClick={() => show(thumbIndex)}
                className={`rounded-8x bg-surface-paper duration-moderate ease-productive relative size-14 overflow-hidden border-2 transition-opacity ${thumbIndex === shown ? "border-brand" : "border-transparent opacity-60 hover:opacity-100"}`}
              />
            ))}
          </div>
        ) : null
      }
    >
      <ViewerReel count={images.length} index={shown} onIndexChange={show} locked={zoomed}>
        {images.map((src, slideIndex) => {
          const active = slideIndex === shown
          return (
            <div key={src} className="relative h-full w-full shrink-0 snap-start snap-always overflow-hidden">
              <IconButton
                ref={active ? stage : undefined}
                aria-label={active && zoomed ? t("gallery.zoomOut") : t("gallery.zoomIn")}
                aria-hidden={active ? undefined : true}
                tabIndex={active ? 0 : -1}
                press={false}
                onClick={toggleZoom}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                className={`ease-expressive duration-moderate absolute inset-0 size-full rounded-none border-0 bg-transparent transition-transform hover:bg-transparent motion-reduce:transition-none ${active && zoomed ? "cursor-zoom-out touch-none" : "cursor-zoom-in touch-pan-x"}`}
                style={active && zoomed ? { transform: `scale(${ZOOM})` } : undefined}
                icon={
                  <TileImage
                    src={src}
                    alt={t("gallery.imageAlt", { title, index: slideIndex + 1 })}
                    fill
                    sizes="200vw"
                    loading={Math.abs(slideIndex - shown) <= 1 ? "eager" : "lazy"}
                    className="object-contain p-500"
                  />
                }
              />
            </div>
          )
        })}
      </ViewerReel>
      {paged && (
        <>
          <IconButton
            aria-label={t("gallery.prevImage")}
            disabled={shown === 0}
            onClick={() => step(-1)}
            icon={<BackIcon size={20} />}
            className="absolute top-1/2 left-300 -translate-y-1/2"
          />
          <IconButton
            aria-label={t("gallery.nextImage")}
            disabled={shown === images.length - 1}
            onClick={() => step(1)}
            icon={<ChevronIcon size={20} />}
            className="absolute top-1/2 right-300 -translate-y-1/2"
          />
        </>
      )}
    </Lightbox>
  )
}

interface ViewerReelProps {
  count: number
  index: number
  onIndexChange: (index: number) => void
  locked: boolean
  children: ReactNode
}

const ViewerReel = ({ count, index, onIndexChange, locked, children }: ViewerReelProps) => {
  const { ref, index: settled, goTo } = useReelPagination(count)
  const start = useRef(index)
  const synced = useRef(false)
  const last = useRef({ index, settled })

  useLayoutEffect(() => {
    const element = ref.current
    const child = element?.children[start.current]
    const first = element?.children[0]
    if (element && child instanceof HTMLElement && first instanceof HTMLElement) element.scrollLeft = child.offsetLeft - first.offsetLeft
  }, [ref])

  useEffect(() => {
    const previous = last.current
    last.current = { index, settled }
    if (!Number.isInteger(settled)) return
    if (!synced.current) {
      synced.current = settled === index
      return
    }
    if (settled !== previous.settled && settled !== index) onIndexChange(settled)
    else if (index !== previous.index && index !== settled) goTo(index)
  })

  return (
    <Reel ref={ref} snap gap="300" className="absolute inset-0 scrollbar-none" style={locked ? { overflowX: "hidden" } : undefined}>
      {children}
    </Reel>
  )
}
